import React from 'react';
import { NumberInputDisplay } from './ValgDisplayFunctions';
import VeilederStegComponent, { VeilederStegBaseProps } from './VeilederStegComponent';
import { EpiAppStateMedSteg } from '../Store/Types';
import { connect } from 'react-redux';
import { setCollapsed } from '../Store/StegVisning/Actions';
import contentResolver from '../Utilities/EpiContentResolver';
import { EpiContext } from '../Store/EpiContext/Types';
import { ValideringsResultat } from '../Models/ValideringsResultat';
import { NumberValidering, validerMinMax } from '../Utilities/Validering';
import LabelWithErrorComponent from './LabelWithErrorComponent';

export interface NumberFormatter {
    // Konverterer tall til en streng formattert for visning
    toFormattetString: (tall: number) => string;
    // Konverterer en streng som er formattert for visning til en streng som kan parses til tall
    toParsableAsNumber: (verdi: string) => string;
}

type NumberInputOwnProps<T> = VeilederStegBaseProps & {
    inputId: string;
    sporsmal: keyof T;
    placeholder: string;
    value?: number;
    postfix: string;
    isSmall?: boolean;
    decimals: number;
    validering: NumberValidering<T>;
    ctaText: keyof T;
    preValidation?: ValideringsResultat;
    validationChecksum?: number;
    numberDisplayFormatter?: NumberFormatter;
    onInputChange: (valg: number) => void;
    onValidationChanged: (result: ValideringsResultat | undefined) => void;
};

type NumberInputProps<T> = NumberInputOwnProps<T> & MapStateToProps<T> & MapDispatchToProps;

interface NumberInputState {
    verdi: number | string;
    parsedVerdi: number | null;
    valideringsResultat?: ValideringsResultat;
    harBekreftetVerdi: boolean;
    validationChecksum?: number;
    isInErrorState: boolean;
}

class NumberInputStegComponent<T> extends React.PureComponent<NumberInputProps<T>, NumberInputState> {
    constructor(props: NumberInputProps<T>) {
        super(props);
        const verdi =
            props.numberDisplayFormatter && props.value
                ? props.numberDisplayFormatter.toFormattetString(props.value)
                : props.value
                ? props.value
                : 0;
        this.state = {
            verdi,
            parsedVerdi: props.value || null,
            valideringsResultat: props.preValidation,
            harBekreftetVerdi: props.value !== undefined,
            validationChecksum: props.validationChecksum,
            isInErrorState: false,
        };
        this.onInputChange = this.onInputChange.bind(this);
        this.confirmValue = this.confirmValue.bind(this);
    }

    onInputKeyUp(e: KeyboardEvent): void {
        if (e.key === 'enter') {
            this.confirmValue();
        }
    }

    confirmValue(): void {
        const {
            onInputChange,
            stegVisningSetCollapsed,
            sporsmalId,
            epiContext,
            validering,
            onValidationChanged,
        } = this.props;
        const { parsedVerdi, verdi } = this.state;
        const resultat = this.valider(parsedVerdi, epiContext, validering);
        onValidationChanged(resultat);
        onInputChange(parsedVerdi !== null ? parsedVerdi : Number(verdi));
        if (resultat && resultat.isValid) {
            this.visErrorMessage(false);
            stegVisningSetCollapsed({ id: sporsmalId, isCollapsed: true });
            this.setState({
                parsedVerdi: parsedVerdi !== null ? parsedVerdi : Number(verdi),
                harBekreftetVerdi: true,
                valideringsResultat: resultat,
            });
        } else {
            this.visErrorMessage(true);
            this.setState({
                valideringsResultat: resultat,
            });
        }
    }

    parse(verdi: string, decimals: number): number {
        if(!verdi) {
            return 0;
        }
        const { numberDisplayFormatter } = this.props;
        const onlyDigits = verdi.replace(/[^(\d+)\.\,]/,''); // removes everything thats not a number, unless its . or ,
        const parsableValue = numberDisplayFormatter
        ? numberDisplayFormatter.toParsableAsNumber(onlyDigits)
        : onlyDigits;
        const parsedStringToFloat = parseFloat(parseFloat(parsableValue).toFixed(decimals));
        return !isNaN(parsedStringToFloat) ? parsedStringToFloat : 0;
    }

    onInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const { decimals, numberDisplayFormatter, validering } = this.props;
        const newValueRaw = this.parse(e.target.value, decimals);
        const newValueFormatted =
            numberDisplayFormatter && newValueRaw
                ? numberDisplayFormatter.toFormattetString(newValueRaw)
                : (newValueRaw == null ? '' : newValueRaw);
        this.setState(prevState => ({
            ...prevState,
            verdi: newValueFormatted,
            parsedVerdi: newValueRaw,
            harBekreftetVerdi: false,
            validationChecksum: validering.max,
        }));
    }

    valider(
        newValueRaw: number | null,
        epiContext: EpiContext<T>,
        validering: NumberValidering<T>,
    ): ValideringsResultat | undefined {
        return validerMinMax(epiContext, newValueRaw != null ? newValueRaw : 0, validering);
    }

    displayElement(
        parsedVerdi: number | null,
        sporsmalId: string,
        numberDisplayFormatter: NumberFormatter | undefined,
        postfix: string,
    ): JSX.Element {
        return parsedVerdi !== null ? (
            NumberInputDisplay(sporsmalId, {
                id: '',
                displayValue: `${
                    numberDisplayFormatter ? numberDisplayFormatter.toFormattetString(parsedVerdi) : parsedVerdi
                } ${postfix}`,
            })
        ) : (
            <div />
        );
    }

    visErrorMessage(show: boolean) {
        document.getElementById(this.props.inputId)?.focus();
        this.setState(previousState => ({...previousState, isInErrorState: show}));
    }

    render(): JSX.Element {
        const {
            postfix,
            inputId,
            sporsmalId,
            tittel,
            forklaring,
            sporsmal,
            isSmall,
            placeholder,
            numberDisplayFormatter,
            epiContext,
            ctaText,
        } = this.props;

        const {
            verdi,
            parsedVerdi,
            valideringsResultat: currentResultat,
            harBekreftetVerdi,
            isInErrorState
        } = this.state;

        return (
            <VeilederStegComponent
                sporsmalId={sporsmalId}
                tittel={tittel}
                forklaring={forklaring}
                inputArea={
                    <div>
                        <div className={`c-veileder__inputomrade ${!isInErrorState ? '' : 'c-veileder__inputomrade--feil'}`}>
                            <LabelWithErrorComponent 
                                inputId={inputId} 
                                showError={isInErrorState && !!currentResultat} 
                                errorElement={currentResultat?.feilElement} >
                                    {contentResolver(epiContext, sporsmal)}
                            </LabelWithErrorComponent>                              
                            <div
                                className={[
                                    'c-number-input',
                                    isSmall || false ? 'c-number-input--small' : 'c-number-input--big',
                                    isInErrorState ? 'c-number-input--feil' : '',
                                ].join(' ')}
                            >
                                <input
                                    className="c-number-input__input"
                                    placeholder={placeholder}
                                    type="text"
                                    id={inputId}
                                    onKeyUp={(e: React.KeyboardEvent): void => {
                                        if (e.key === 'Enter') {
                                            this.confirmValue();
                                        }
                                    }}
                                    onChange={this.onInputChange}
                                    value={verdi}
                                />
                                <div className="c-number-input__postfix">
                                    <span>{postfix}</span>
                                </div>
                            </div>
                        </div>
                        <div className="c-veileder__cta">
                            <button
                                onClick={this.confirmValue}
                                className="c-knapp c-knapp--cta c-knapp--secondary"
                            >
                                {contentResolver(epiContext, ctaText)}
                            </button>
                        </div>
                    </div>                
                }
                displayElement={this.displayElement(parsedVerdi, sporsmalId, numberDisplayFormatter, postfix)}
                canCollapse={harBekreftetVerdi && !isInErrorState}
                neverCollapse={false}
            />
        );
    }
}

interface MapStateToProps<T> {
    epiContext: EpiContext<T>;
}

interface MapDispatchToProps {
    stegVisningSetCollapsed: typeof setCollapsed;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function NumberInputStegContainer<T>() {
    return connect(
        (state: EpiAppStateMedSteg<T>): MapStateToProps<T> => {
            return {
                epiContext: state.epiContext,
            };
        },
        { stegVisningSetCollapsed: setCollapsed },
    )(NumberInputStegComponent as new (props: NumberInputProps<T>) => NumberInputStegComponent<T>);
}
